import { FormControl, InputLabel, MenuItem, Select, TextField } from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { Button, Modal } from '@vadiun/react-components';
import DayJsUtils from '@date-io/moment';
import { Field, Form, Formik } from 'formik';
import { KeyboardDateTimePicker } from 'formik-material-ui-pickers';
import moment, { Moment } from 'moment';
import { useSnackbar } from 'notistack';
import { FC, useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import {
  Contingency,
  ContingencyReason,
  ContingencyType,
  contingencyService
} from '../services/contingencyService';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  contingency?: Contingency | null;
  onSuccess?: () => void;
};

type FormType = {
  contingency_reason_id: number | '';
  contingency_type_id: number | '';
  applied_at: Moment;
  estimated_duration_minutes: number | '';
  observation: string;
};

const Schema = Yup.object().shape({
  contingency_reason_id: Yup.number().required('Seleccione una razón'),
  contingency_type_id: Yup.number().required('Seleccione un tipo'),
  applied_at: Yup.mixed().required('Ingrese la hora de inicio'),
  estimated_duration_minutes: Yup.number().required('Seleccione la duración'),
  observation: Yup.string()
});

const DURATION_OPTIONS = [5, 10, 15, 30];

const computeDurationMinutes = (
  appliedAt?: string | null,
  finishedAt?: string | null
): number | '' => {
  if (!appliedAt || !finishedAt) return '';
  const start = moment(appliedAt);
  const end = moment(finishedAt);
  if (!start.isValid() || !end.isValid()) return '';
  const diff = end.diff(start, 'minutes');
  return DURATION_OPTIONS.includes(diff) ? diff : '';
};

const ContingencyModal: FC<Props> = ({
  isOpen,
  onClose,
  contingency,
  onSuccess
}) => {
  const [reasons, setReasons] = useState<ContingencyReason[]>([]);
  const [types, setTypes] = useState<ContingencyType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const snackbar = useSnackbar();
  const service = contingencyService();

  const isEdit = !!contingency;

  useEffect(() => {
    if (!isOpen) return;
    service.getContingencyReasons().then(setReasons).catch(() => {});
    service.getContingencyTypes().then(setTypes).catch(() => {});
  }, [isOpen]);

  const initialValues = useMemo<FormType>(() => {
    if (contingency) {
      return {
        contingency_reason_id: contingency.contingency_reason_id ?? '',
        contingency_type_id: contingency.contingency_type_id ?? '',
        applied_at: contingency.applied_at
          ? moment(contingency.applied_at)
          : moment(),
        estimated_duration_minutes: computeDurationMinutes(
          contingency.applied_at,
          contingency.finished_at
        ),
        observation: contingency.observation ?? ''
      };
    }
    return {
      contingency_reason_id: '',
      contingency_type_id: '',
      applied_at: moment(),
      estimated_duration_minutes: '',
      observation: ''
    };
  }, [contingency]);

  const handleSubmit = async (values: FormType, { resetForm }) => {
    try {
      setIsLoading(true);
      const payload = {
        contingency_reason_id: values.contingency_reason_id as number,
        contingency_type_id: values.contingency_type_id as number,
        applied_at: (values.applied_at as Moment).toISOString(),
        estimated_duration_minutes: values.estimated_duration_minutes as number,
        observation: values.observation || null
      };
      if (isEdit && contingency) {
        await service.updateContingency(contingency.id, payload);
        snackbar.enqueueSnackbar('Contingencia actualizada correctamente', {
          variant: 'success'
        });
      } else {
        await service.createContingency({
          contingency_reason_id: payload.contingency_reason_id,
          contingency_type_id: payload.contingency_type_id,
          applied_at: payload.applied_at,
          estimated_duration_minutes: payload.estimated_duration_minutes
        });
        snackbar.enqueueSnackbar('Contingencia creada correctamente', {
          variant: 'success'
        });
      }
      resetForm();
      onSuccess?.();
      onClose();
    } catch {
      snackbar.enqueueSnackbar(
        isEdit
          ? 'Error al actualizar la contingencia'
          : 'Error al crear la contingencia',
        { variant: 'error' }
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="sm"
      body={
        <>
          <p className="font-bold text-lg mb-6">
            {isEdit ? 'Editar contingencia' : 'Crear contingencia'}
          </p>
          <Formik<FormType>
            enableReinitialize
            initialValues={initialValues}
            validationSchema={Schema}
            onSubmit={handleSubmit}
          >
            {({ touched, errors, values, setFieldValue }) => (
              <Form className="flex flex-col gap-4">
                <FormControl
                  variant="outlined"
                  fullWidth
                  error={
                    touched.contingency_reason_id &&
                    Boolean(errors.contingency_reason_id)
                  }
                >
                  <InputLabel id="contingency-reason-label">
                    Razón de la contingencia
                  </InputLabel>
                  <Select
                    labelId="contingency-reason-label"
                    label="Razón de la contingencia"
                    value={values.contingency_reason_id}
                    onChange={(e) =>
                      setFieldValue('contingency_reason_id', e.target.value)
                    }
                  >
                    {reasons.map(({ id, label }) => (
                      <MenuItem value={id} key={id}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.contingency_reason_id &&
                    errors.contingency_reason_id && (
                      <p className="text-red-500 text-xs mt-1 ml-3">
                        {errors.contingency_reason_id}
                      </p>
                    )}
                </FormControl>

                <FormControl
                  variant="outlined"
                  fullWidth
                  error={
                    touched.contingency_type_id &&
                    Boolean(errors.contingency_type_id)
                  }
                >
                  <InputLabel id="contingency-type-label">
                    Tipo de contingencia
                  </InputLabel>
                  <Select
                    labelId="contingency-type-label"
                    label="Tipo de contingencia"
                    value={values.contingency_type_id}
                    onChange={(e) =>
                      setFieldValue('contingency_type_id', e.target.value)
                    }
                  >
                    {types.map(({ id, label }) => (
                      <MenuItem value={id} key={id}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.contingency_type_id &&
                    errors.contingency_type_id && (
                      <p className="text-red-500 text-xs mt-1 ml-3">
                        {errors.contingency_type_id}
                      </p>
                    )}
                </FormControl>

                <MuiPickersUtilsProvider utils={DayJsUtils}>
                  <Field
                    name="applied_at"
                    label="Hora de inicio"
                    inputVariant="outlined"
                    component={KeyboardDateTimePicker}
                    fullWidth
                    ampm={false}
                    format="DD/MM/YYYY HH:mm"
                  />
                </MuiPickersUtilsProvider>

                <FormControl
                  variant="outlined"
                  fullWidth
                  error={
                    touched.estimated_duration_minutes &&
                    Boolean(errors.estimated_duration_minutes)
                  }
                >
                  <InputLabel id="duration-label">
                    Duración (minutos)
                  </InputLabel>
                  <Select
                    labelId="duration-label"
                    label="Duración (minutos)"
                    value={values.estimated_duration_minutes}
                    onChange={(e) =>
                      setFieldValue(
                        'estimated_duration_minutes',
                        e.target.value
                      )
                    }
                  >
                    {DURATION_OPTIONS.map((mins) => (
                      <MenuItem value={mins} key={mins}>
                        {mins} minutos
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.estimated_duration_minutes &&
                    errors.estimated_duration_minutes && (
                      <p className="text-red-500 text-xs mt-1 ml-3">
                        {errors.estimated_duration_minutes}
                      </p>
                    )}
                </FormControl>

                <TextField
                  variant="outlined"
                  fullWidth
                  label="Observación"
                  multiline
                  minRows={2}
                  value={values.observation}
                  onChange={(e) => setFieldValue('observation', e.target.value)}
                />

                <div className="mt-4 flex justify-end gap-2">
                  <Button onClick={onClose} variant="text">
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    isLoading={isLoading}
                  >
                    {isEdit ? 'Guardar' : 'Crear'}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </>
      }
    />
  );
};

export default ContingencyModal;
